<template>
  <BaseModalEditCreate
    :mode="mode"
    :is-modified="isModified"
    :hide-save-button="!modifiedData.isFromManager"
    @save="onSave"
    @dismiss-changes="onDismissChanges">
    <template #form>
      <div :class="{ 'pointer-events-none': !modifiedData.isFromManager }">
        <label for="name" class="text-sm font-medium">{{ $t('baseObject.name') }}</label>
        <BaseInput
          v-model="modifiedData.name"
          color="base"
          show-error-as-tooltip
          :error="$v.name.required.$invalid ? $v.name.required.$message : ''" />
      </div>
      <div :class="{ 'pointer-events-none': !modifiedData.isFromManager }">
        <label for="name" class="text-sm font-medium">{{ $t('question.sort') }}</label>
        <BaseInput v-model="modifiedData.sort" type="number" color="base" flat />
      </div>

      <BaseToggle
        :label="$t('missedVisitReasons.dueToSalesperson')"
        name="salesperson"
        v-model="modifiedData.dueToSalesPerson"
        class="py-1"
        :class="{ 'pointer-events-none': !modifiedData.isFromManager }" />

      <BaseToggle
        :label="$t('missedVisitReasons.dueToTraderSite')"
        name="tradersite"
        v-model="modifiedData.dueToTraderSite"
        class="py-1"
        :class="{ 'pointer-events-none': !modifiedData.isFromManager }" />
      <BaseToggle
        :label="$t('missedVisitReasons.dueToPlan')"
        name="plan"
        v-model="modifiedData.dueToPlan"
        class="py-1"
        :class="{ 'pointer-events-none': !modifiedData.isFromManager }" />

      <BaseToggle
        :label="$t('baseObject.isActive')"
        name="isActive"
        v-model="modifiedData.isActive"
        class="py-1"
        :class="{ 'pointer-events-none': !modifiedData.isFromManager }" />
    </template>
  </BaseModalEditCreate>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useToastStore } from '@/stores/toast';
import { useI18n } from 'vue-i18n';
import { useVuelidate } from '@vuelidate/core';
import { required, helpers } from '@vuelidate/validators';

import BaseInput from '@/components/common/BaseInput.vue';
import BaseToggle from '@/components/common/BaseToggle.vue';
import BaseModalEditCreate from '@/components/common/BaseModalEditCreate.vue';

import type { MissedVisitsReasons } from '@/types/Routes';
import { editMissedVisitReason } from '@/services/api/routes';
import { deepCopy } from '@/utils/data';

const { t: $t } = useI18n({ useScope: 'global' });

const emit = defineEmits(['closeModal', 'save']);

const props = withDefaults(
  defineProps<{
    mode: 'add' | 'edit';
    missedVisitReason: MissedVisitsReasons;
  }>(),
  {}
);

const modifiedData = ref<MissedVisitsReasons>(
  deepCopy<MissedVisitsReasons>(props.missedVisitReason)
);
const isModified = ref<boolean>(false);

watch(
  () => modifiedData.value,
  (newData) => {
    validationModel.value.name = newData.name || '';

    isModified.value = JSON.stringify(newData) !== JSON.stringify(props.missedVisitReason);
  },
  { deep: true }
);

function onDismissChanges() {
  modifiedData.value = deepCopy<MissedVisitsReasons>(props.missedVisitReason);
  emit('closeModal');
}

const toastStore = useToastStore();

async function onSave() {
  $v.value.$touch();
  if ($v.value.$invalid) return;
  const res = await editMissedVisitReason(modifiedData.value);
  if (res) {
    emit('save', modifiedData.value);
    toastStore.success({ text: `${$t('common.toast.success')}` });
  } else {
    toastStore.error({ text: `${$t('common.toast.error')}` });
    emit('closeModal');
  }
}

const validationModel = ref({
  name: modifiedData.value.name || '',
});

const rules = computed(() => ({
  name: { required: helpers.withMessage($t('messages.errors.requiredField'), required) },
}));

const $v = useVuelidate(rules, validationModel, { $lazy: true });
</script>
